import React, { useState } from "react";
import {
  Calculator,
  Plus,
  Minus,
  Wallet,
  Landmark,
  CreditCard,
  AlertTriangle,
  Info,
} from "lucide-react";
import { fmtMoney } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import HireUsCTA from "@/components/HireUsCTA";
import EmailResultsCard from "@/components/EmailResultsCard";
import NudgeCard from "@/components/NudgeCard";

export default function GstCalc() {
  const { t } = useLang();
  const [amount, setAmount] = useState(1000);
  const [mode, setMode] = useState("add"); // 'add' = exclusive -> inclusive, 'remove' = inclusive -> exclusive

  const GST = 0.15;
  const a = Number(amount) || 0;
  const result =
    mode === "add"
      ? { ex: a, gst: a * GST, inc: a * (1 + GST) }
      : { ex: a / (1 + GST), gst: a - a / (1 + GST), inc: a };

  return (
    <div className="stagger-in max-w-5xl">
      <div className="label-mono">/ {t("common.calculator", "CALCULATOR")} · 01</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">{t("gst.h1", "GST · 15%")}</h1>
      <p className="text-zinc-400 mt-2 max-w-2xl">
        {t("gst.sub", "Add GST to an exclusive amount, or pull it out of an inclusive one.")}
      </p>

      {/* PLAIN-ENGLISH EXPLAINER */}
      <div
        className="mt-8 grid md:grid-cols-3 gap-0 border border-[var(--border)]"
        data-testid="gst-explainer"
      >
        <div className="p-6 border-r border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="text-[var(--primary)]" />
            <div className="label-mono">Your money</div>
          </div>
          <div className="font-display text-xl mt-2">GST exclusive</div>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
            This is <span className="text-white font-semibold">yours to keep</span>.
            It pays you, your wages, your tools, and your profit.
          </p>
        </div>
        <div className="p-6 border-r border-[var(--border)] bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Landmark size={16} className="text-[var(--secondary)]" />
            <div className="label-mono">IRD's money</div>
          </div>
          <div className="font-display text-xl mt-2">GST · 15%</div>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
            You're just <span className="text-white font-semibold">collecting it</span>{" "}
            for the government. Don't spend it — IRD wants it back.
          </p>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2">
            <CreditCard size={16} className="text-white" />
            <div className="label-mono">Customer pays</div>
          </div>
          <div className="font-display text-xl mt-2">GST inclusive</div>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
            The full amount that <span className="text-white font-semibold">leaves their wallet</span>.
            Your money + IRD's money, together.
          </p>
        </div>
      </div>

      {/* CALCULATOR */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-0 mt-10 border border-[var(--border)]">
        <div className="md:col-span-3 p-4 sm:p-6 md:p-8 border-r border-[var(--border)]">
          <div className="label-mono mb-3">Mode</div>
          <div className="grid grid-cols-2 gap-0 border border-[var(--border)] mb-8">
            <button
              onClick={() => setMode("add")}
              data-testid="gst-mode-add"
              className={`px-4 py-3 font-mono text-xs uppercase tracking-wider flex items-center justify-center gap-2 ${
                mode === "add"
                  ? "bg-[var(--primary)] text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              <Plus size={14} /> {t("gst.mode.add", "Add GST")}
            </button>
            <button
              onClick={() => setMode("remove")}
              data-testid="gst-mode-remove"
              className={`px-4 py-3 font-mono text-xs uppercase tracking-wider flex items-center justify-center gap-2 border-l border-[var(--border)] ${
                mode === "remove"
                  ? "bg-[var(--primary)] text-black"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              <Minus size={14} /> {t("gst.mode.remove", "Remove GST")}
            </button>
          </div>

          <div className="label-mono mb-3">
            {mode === "add" ? t("gst.label.amount_excl", "Amount (excl. GST)") : t("gst.label.amount_incl", "Amount (incl. GST)")}
          </div>
          <input
            type="number"
            step="0.01"
            className="input-sharp text-2xl py-4"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="gst-amount-input"
          />
          <div className="text-xs font-mono text-zinc-500 mt-2">NZD</div>

          <div
            className="mt-6 flex items-start gap-2 text-xs font-mono text-zinc-400 leading-relaxed"
            data-testid="gst-mode-hint"
          >
            <Info size={12} className="mt-0.5 shrink-0 text-[var(--primary)]" />
            <div>
              {mode === "add"
                ? t("gst.mode.add_hint", "ADD MODE: Punch in your sticker price (your money). We'll add 15% GST on top — that's what you charge the customer.")
                : t("gst.mode.remove_hint", "REMOVE MODE: Punch in what the customer paid total. We'll pull the 15% IRD slice out so you can see what's actually yours.")}
            </div>
          </div>
        </div>

        {/* RECEIPT WITH INLINE COMMENTS */}
        <div className="md:col-span-2 p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
          <div className="flex items-center gap-2 mb-4">
            <Calculator size={16} className="text-[var(--primary)]" />
            <div className="label-mono">{t("gst.receipt", "Receipt")}</div>
          </div>
          <div className="space-y-5 font-mono">
            <ExplainedRow
              label={t("gst.excl", "Excl. GST")}
              value={fmtMoney(result.ex)}
              testid="gst-ex"
              icon={Wallet}
              tone="primary"
              note={t("gst.note.excl", "This is your money — what you actually earn.")}
            />
            <ExplainedRow
              label={t("gst.gst15", "GST 15%")}
              value={fmtMoney(result.gst)}
              testid="gst-gst"
              icon={Landmark}
              tone="secondary"
              note={t("gst.note.gst", "This belongs to IRD. You're just holding it for them.")}
            />
            <div className="border-t border-[var(--border)] pt-4">
              <ExplainedRow
                label={t("gst.incl", "Incl. GST")}
                value={fmtMoney(result.inc)}
                testid="gst-inc"
                icon={CreditCard}
                tone="white"
                big
                note={t("gst.note.incl", "This is what the customer pays in total.")}
              />
            </div>
          </div>
          <div className="hi-vis-stripe h-1.5 mt-8 -mx-4 sm:-mx-6 md:-mx-8 -mb-4 sm:-mb-6 md:-mb-8" />
        </div>
      </div>

      {/* FRIENDLY WARNING */}
      <div
        className="mt-8 border border-[var(--secondary)]/40 bg-[var(--secondary)]/5 p-5 flex items-start gap-3"
        data-testid="gst-warning"
      >
        <AlertTriangle
          size={18}
          className="text-[var(--secondary)] shrink-0 mt-0.5"
        />
        <div className="text-sm text-zinc-200 leading-relaxed">
          <strong className="text-white">Cashflow rule of thumb:</strong> when a
          customer pays an invoice, only{" "}
          <span className="text-[var(--primary)] font-mono">86.96%</span> of that
          money is actually yours — the other{" "}
          <span className="text-[var(--secondary)] font-mono">13.04%</span> is GST
          you have to pay IRD next return. Treat your business account like two
          piggy banks: yours, and IRD's. Don't dip into IRD's.
        </div>
      </div>

      {/* HOW GST WORKS — KID FRIENDLY */}
      <div className="mt-8 grid md:grid-cols-3 gap-0 border border-[var(--border)]">
        <div className="p-6 border-r border-[var(--border)]">
          <div className="font-display text-2xl text-[var(--primary)]">01</div>
          <div className="font-display text-lg mt-2">You charge</div>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
            You quote a job for $1,000. You add 15% GST — so the customer's
            invoice says <span className="font-mono text-white">$1,150</span>.
          </p>
        </div>
        <div className="p-6 border-r border-[var(--border)]">
          <div className="font-display text-2xl text-[var(--primary)]">02</div>
          <div className="font-display text-lg mt-2">You collect</div>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
            Customer pays you <span className="font-mono text-white">$1,150</span>.
            Of that, <span className="font-mono text-[var(--primary)]">$1,000</span>{" "}
            is yours and <span className="font-mono text-[var(--secondary)]">$150</span>{" "}
            is IRD's.
          </p>
        </div>
        <div className="p-6">
          <div className="font-display text-2xl text-[var(--primary)]">03</div>
          <div className="font-display text-lg mt-2">You pay IRD</div>
          <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
            At GST time you send the{" "}
            <span className="font-mono text-[var(--secondary)]">$150</span> to IRD
            (less any GST you paid on tools/materials). The{" "}
            <span className="font-mono text-[var(--primary)]">$1,000</span> stays
            with you.
          </p>
        </div>
      </div>

      <div className="mt-6 text-xs font-mono text-zinc-500 max-w-3xl leading-relaxed">
        You only have to register for GST once your turnover is over{" "}
        <span className="text-zinc-300">$60,000 per year</span>. Below that, no GST
        on your invoices — and nothing to send IRD.
      </div>

      <NudgeCard
        show={mode === "remove"}
        tone="warning"
        title="GST returns getting messy?"
        body="Removing GST from a customer payment is fine — filing an actual GST return with IRD every 1, 2 or 6 months is the painful bit. We do this for our $75/mo Growth subscribers."
        cta="See GST plans"
        campaign="gst-calc-nudge"
      />

      <EmailResultsCard
        calculator="gst"
        payload={{ mode, amount: a, ex: result.ex, gst: result.gst, inc: result.inc }}
        labelKey="email.gst.label"
        descKey="email.gst.desc"
      />

      <HireUsCTA campaign="gst-calc" compact />
    </div>
  );
}

function ExplainedRow({ label, value, big, tone = "white", icon: Icon, note, testid }) {
  const valueColor =
    tone === "primary"
      ? "text-[var(--primary)]"
      : tone === "secondary"
      ? "text-[var(--secondary)]"
      : "text-white";
  const iconColor =
    tone === "primary"
      ? "text-[var(--primary)]"
      : tone === "secondary"
      ? "text-[var(--secondary)]"
      : "text-zinc-400";
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="flex items-center gap-2 label-mono">
          {Icon && <Icon size={12} className={iconColor} />}
          {label}
        </span>
        <span
          data-testid={testid}
          className={`font-mono ${big ? "text-3xl" : "text-lg"} ${valueColor}`}
        >
          {value}
        </span>
      </div>
      {note && (
        <div className="text-[11px] font-mono text-zinc-500 mt-1.5 leading-snug pl-5">
          {note}
        </div>
      )}
    </div>
  );
}
